import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Skeleton,
    Avatar
} from '@mui/material';
import {
    Link as LinkIcon,
    ContentCopy as ContentCopyIcon,
    ShoppingCart as ShoppingCartIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Wallet as WalletIcon
} from '@mui/icons-material';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { QRCodeSVG } from 'qrcode.react';
import SubscriptionService from '../../../../services/SubscriptionService';
import ResponseService from '../../../../services/ResponseService';
import { Campaign } from '../../../../types/Campaign';
import { SubscriptionStatus } from '../../../../types/Subscription';
import { ResponseMetricsDTO } from '../../../../types/Repsonse';

interface ApplicationDashboardProps {
    campaign: Campaign;
}

// Color mapping for response metrics
const METRIC_COLORS = {
    submitted_mentors: '#1976d2',
    pending_mentors: '#ff9800',
    submitted_mentees: '#2e7d32',
    pending_mentees: '#f57c00'
};

const ApplicationDashboard: React.FC<ApplicationDashboardProps> = ({ campaign }) => {
    // Subscription state
    const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
    const [loadingSubscription, setLoadingSubscription] = useState<boolean>(true);
    const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
    const [creditsPanelExpanded, setCreditsPanelExpanded] = useState(false);
    
    // Application link loading state
    const [linkLoading, setLinkLoading] = useState<boolean>(false);
    
    // Response metrics state
    const [responseMetrics, setResponseMetrics] = useState<ResponseMetricsDTO | null>(null);
    const [metricsLoading, setMetricsLoading] = useState<boolean>(true);
    const [metricsError, setMetricsError] = useState<string | null>(null);
    
    // Computed values
    const creditsRemaining = subscriptionStatus?.seat_credits ?? 0;
    const applicationUrl = `https://apply.mentoringfusion.com/${campaign.campaign_code}`;
    
    // Transform response metrics to chart data
    const applicationData = responseMetrics ? [
        { id: 0, value: responseMetrics.submitted_mentors, name: 'Submitted Mentors', color: METRIC_COLORS.submitted_mentors },
        { id: 1, value: responseMetrics.pending_mentors, name: 'Pending Mentors', color: METRIC_COLORS.pending_mentors },
        { id: 2, value: responseMetrics.submitted_mentees, name: 'Submitted Mentees', color: METRIC_COLORS.submitted_mentees },
        { id: 3, value: responseMetrics.pending_mentees, name: 'Pending Mentees', color: METRIC_COLORS.pending_mentees }
    ].filter(item => item.value > 0) : []; // Only show non-zero values
    
    // Fetch response metrics and campaign code
    useEffect(() => {
        if (campaign && campaign.id && campaign.organization_id) {
            // Fetch response metrics
            setMetricsLoading(true);
            ResponseService.getResponseMetrics(campaign.organization_id, campaign.id)
                .then((metrics: ResponseMetricsDTO) => {
                    if (metrics) {
                        setResponseMetrics(metrics);
                    }
                })
                .catch((err: Error) => {
                    console.error('Error fetching response metrics:', err);
                    setMetricsError('Failed to load response metrics');
                })
                .finally(() => {
                    setMetricsLoading(false);
                    setLinkLoading(false);
                });
        }
    }, [campaign]);
    
    // Fetch subscription data
    useEffect(() => {
        if (campaign && campaign.organization_id) {
            setLoadingSubscription(true);
            SubscriptionService.getSubscriptionStatus(campaign.organization_id)
                .then((subscriptionData: SubscriptionStatus) => {
                    setSubscriptionStatus(subscriptionData);
                })
                .catch((err: Error) => {
                    console.error('Error fetching subscription status:', err);
                    setSubscriptionError('Failed to load subscription data');
                })
                .finally(() => {
                    setLoadingSubscription(false);
                });
        }
    }, [campaign]);
    
    const handleCopyLink = () => {
        navigator.clipboard.writeText(applicationUrl);
        // You could add a toast notification here
    };

    const handleSaveQRCode = () => {
        // Create a canvas element to convert SVG to image
        const svg = document.querySelector('#qr-code-svg') as SVGElement;
        if (!svg) return;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        // Set canvas size
        canvas.width = 200;
        canvas.height = 200;
        
        // Convert SVG to data URL
        const svgData = new XMLSerializer().serializeToString(svg);
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);
        
        img.onload = () => {
            ctx?.drawImage(img, 0, 0, 200, 200);
            
            // Create download link
            const link = document.createElement('a');
            link.download = `${campaign.campaign_name}-qr-code.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
            
            URL.revokeObjectURL(url);
        };
        
        img.src = url;
    };
    
    const toggleCreditsPanel = () => {
        setCreditsPanelExpanded(!creditsPanelExpanded);
    };
    
    const handlePurchaseCredits = () => {
        // TODO: Implement credit purchase flow
        console.log('Purchase credits clicked');
    };
    
    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Application Dashboard
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Manage applications for {campaign.campaign_name}
                </Typography>
            </Box>
            
            {/* Application Link and QR Code */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                <LinkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                Application Link
                            </Typography>
                            {linkLoading ? (
                                <Skeleton variant="text" width={300} height={24} />
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            bgcolor: 'grey.100',
                                            p: 1,
                                            borderRadius: 1,
                                            flex: 1,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}
                                    >
                                        {applicationUrl}
                                    </Typography>
                                    <Button
                                        size="small"
                                        startIcon={<ContentCopyIcon />}
                                        onClick={handleCopyLink}
                                    >
                                        Copy
                                    </Button>
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ ml: 3, textAlign: 'center' }}>
                            {!linkLoading && (
                                <>
                                    <QRCodeSVG id="qr-code-svg" value={applicationUrl} size={80} />
                                    <Box sx={{ mt: 1 }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={handleSaveQRCode}
                                            sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                                        >
                                            Save QR
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>
                </CardContent>
            </Card>
            
            {/* Subscription Credits */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: creditsRemaining < 50 ? 'error.main' : 'primary.main' }}>
                                <WalletIcon />
                            </Avatar>
                            <Box>
                                {loadingSubscription ? (
                                    <>
                                        <Skeleton variant="text" width={200} height={32} />
                                        <Skeleton variant="text" width={150} height={20} />
                                    </>
                                ) : subscriptionError ? (
                                    <>
                                        <Typography variant="h6" color="error">
                                            Error loading credits
                                        </Typography>
                                        <Typography variant="body2" color="error">
                                            {subscriptionError}
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <Typography variant="h6">
                                            Credits Remaining: {creditsRemaining}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {creditsRemaining < 50 ? 'Low credits - consider purchasing more' : 'You have sufficient credits'}
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="contained"
                                startIcon={<ShoppingCartIcon />}
                                onClick={handlePurchaseCredits}
                                color={creditsRemaining < 50 ? 'error' : 'primary'}
                            >
                                Purchase Credits
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={creditsPanelExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                onClick={toggleCreditsPanel}
                            >
                                {creditsPanelExpanded ? 'Hide' : 'Show'} Details
                            </Button>
                        </Box>
                    </Box>
                    
                    {creditsPanelExpanded && (
                        <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
                            <Typography variant="body2" color="text.secondary">
                                Each application review costs 1 credit. Purchase additional credits to continue reviewing applications.
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>
            
            {/* Applications Breakdown */}
            <Card>
                <CardContent>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" gutterBottom>
                            Application Breakdown
                        </Typography>
                        
                        {metricsLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Skeleton variant="circular" width={200} height={200} />
                            </Box>
                        ) : metricsError ? (
                            <Box sx={{ mt: 2, p: 2, bgcolor: '#ffebee', borderRadius: 1 }}>
                                <Typography variant="body2" color="error">
                                    {metricsError}
                                </Typography>
                            </Box>
                        ) : applicationData.length === 0 ? (
                            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    No application data available yet
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <ResponsiveContainer width={500} height={300}>
                                    <PieChart>
                                        <Pie
                                            data={applicationData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={({ name, value }: any) => `${name}: ${value}`}
                                            outerRadius={80}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {applicationData.map((entry: { id: number; value: number; name: string; color: string }, index: number) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                        )}
                </Box>
            </CardContent>
        </Card>
    </Box>
    );
};

export default ApplicationDashboard;
